import React, { useEffect } from "react";
import ActionBar from "../actionBar/ActionBar";
import AiAssessmentContent from "./AiAssessmentContent";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import AiAssessmentProctoring from "./AiAssessmentProctoring";
import RightClickDisabledModal from "../modals/RightClickDisabledModal";
import CursorFocusChangeModal from "../modals/CursorFocusChangeModal";
import TabSwitchModal from "../modals/TabSwitchModal";
import SuspiciousKeyPressModal from "../modals/SuspiciousKeyPressModal";
import FullScreenExitModal from "../modals/FullScreenExitModal";
import DeviceSetupWizard from "../DeviceSetupWizard";
import RetryAiAssessmentModal from "../modals/RetryAiAssessmentModal";
import CamRecorder from "../../../../Components/CamRecorder/CamRecorder";
import Draggable from "react-draggable";
import AiAssessmentOfflineMessage from "./AiAssessmentOfflineMessage";
import usePing from "../../hooks/usePing";
import StartAIAssessment from "../startAIAssessment/StartAIAssessment";
import useAiAssessmentStart from "../../hooks/useAiAssessmentStart";
import "./aiAssessment.scss";

interface AiAssessmentProps {
  isSameDevice: boolean;
  terminatedStats: {
    isLimitReached: boolean;
    violationType: string;
  };
}
const AiAssessment: React.FC<AiAssessmentProps> = ({
  isSameDevice,
  terminatedStats,
}) => {
  const {
    currentStep,
    fullscreenElementRef,
    isFullscreen,
    showCamRecorder,
    showPermissionConfirmationModal,
    cameraMicrophoneStreamRef,
    displayStreamRef,
  } = useAIAssessmentContext();

  const { handlePermissionCancel, handlePermissionAllow } =
    useAiAssessmentStart();

  // HeartBeatMonitoring
  usePing();

  // Cleanup media streams when component unmounts
  useEffect(() => {
    return () => {
      // Stop camera and microphone stream
      if (cameraMicrophoneStreamRef?.current) {
        try {
          cameraMicrophoneStreamRef.current.getTracks().forEach((track) => {
            track.stop();
          });
          cameraMicrophoneStreamRef.current = null;
        } catch (error) {
          console.warn(
            "Error stopping camera/microphone stream on unmount:",
            error,
          );
        }
      }

      // Stop display/screen sharing stream
      if (displayStreamRef?.current) {
        try {
          displayStreamRef.current.getTracks().forEach((track) => {
            track.stop();
          });
          displayStreamRef.current = null;
        } catch (error) {
          console.warn("Error stopping display stream on unmount:", error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* AI ASSESSMENT PROCTORING */}
      <AiAssessmentProctoring />

      {/* AI ASSESSMENT FULLSCREEN CONTAINER */}
      <section
        className={`ai-assessment-container ${
          isFullscreen ? "ai-assessment-container-fullscreen" : ""
        }`}
        ref={fullscreenElementRef}
      >
        {/* AI ASSESSMENT OFFLINE MESSAGE */}
        <AiAssessmentOfflineMessage />
        {/* ACTION BAR */}
        {currentStep >= 0 && (
          <div className="ai-assessment-action-bar-container">
            <ActionBar />
          </div>
        )}

        {/* AI ASSESSMENT CONTENT */}
        <div className="ai-assessment-content-container">
          <AiAssessmentContent />
          {/* CAMERA RECORDER */}
          {showCamRecorder && (
            <Draggable bounds="parent">
              <div className="video-recorder-container">
                <CamRecorder />
              </div>
            </Draggable>
          )}
        </div>

        {/* AI ASSESSMENT MODALS */}
        <RightClickDisabledModal />
        <CursorFocusChangeModal />
        <TabSwitchModal />
        <SuspiciousKeyPressModal />
        <FullScreenExitModal />
        <RetryAiAssessmentModal />
      </section>

      {/* DEVICE SETUP WIZARD - Full Screen */}
      {showPermissionConfirmationModal && (
        <DeviceSetupWizard
          onComplete={handlePermissionAllow}
          onCancel={handlePermissionCancel}
        />
      )}

      {/* START AI ASSESSMENT Screen - Commented out, functionality moved to DeviceSetupWizard */}
      {/* <StartAIAssessment /> */}
    </>
  );
};

export default AiAssessment;
